<template>
  <div class="TheSidebar">
    <transition name="fade">
      <UIModalOverlay
        v-if="isSidebarVisible && isMobileView"
        class="TheSidebar__overlay"
        @click="$emit('toggle')"
      />
    </transition>

    <transition name="slide-fade" @afterLeave="emit('afterLeave')">
      <aside v-show="isSidebarVisible" class="TheSidebar__content">
        <TheSidebarHeader @toggle="toggleSidebar()" />

        <nav class="TheSidebar__nav" aria-label="Primary navigation">
          <section class="TheSidebar__section TheSidebar__section--top">
            <div class="TheSidebar__section-content">
              <MenuItemNewChat />
              <MenuItemSearch />
            </div>
          </section>

          <div class="TheSidebar__scrollArea">
            <section class="TheSidebar__section">
              <h2 class="TheSidebar__section-title">AI Tools</h2>
              <div class="TheSidebar__section-content">
                <MenuItemGpts />
                <MenuItemLibrary />
                <MenuItemConnectors v-if="isMcpConnectorsEnabled" />
              </div>
            </section>

            <PinnedChatHistory
              class="TheSidebar__section"
              @onChatClick="isMobileView ? toggleSidebar() : null"
            />

            <section class="TheSidebar__section">
              <h2 class="TheSidebar__section-title">Chats</h2>
              <div class="TheSidebar__section-content">
                <ChatHistory
                  :chats="combinedChatList"
                  @onChatClick="isMobileView ? toggleSidebar() : null"
                />
              </div>
            </section>
          </div>
        </nav>

        <TheSidebarUserMenu />
      </aside>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'

import { UIModalOverlay } from '@foundever/ui-lib'
import { useWindowSize } from '@vueuse/core'
import { storeToRefs } from 'pinia'

import { useEventListeners } from '@/composables/useEventListeners'
import { PosthogFeatureFlags, usePosthogFlag } from '@/lib/posthog'
import { useChatStore } from '@/store/chat'

import ChatHistory from './components/ChatHistory.vue'
import MenuItemConnectors from './components/menuItems/MenuItemConnectors.vue'
import MenuItemGpts from './components/menuItems/MenuItemGpts.vue'
import MenuItemLibrary from './components/menuItems/MenuItemLibrary.vue'
import MenuItemNewChat from './components/menuItems/MenuItemNewChat.vue'
import MenuItemSearch from './components/menuItems/MenuItemSearch.vue'
import PinnedChatHistory from './components/PinnedChatHistory.vue'
import TheSidebarHeader from './components/TheSidebarHeader.vue'
import TheSidebarUserMenu from './components/TheSidebarUserMenu.vue'

const emit = defineEmits(['toggle', 'afterLeave'])
const props = withDefaults(
  defineProps<{
    isOpen?: boolean
  }>(),
  {
    isOpen: true,
  },
)

const toggleSidebar = () => {
  emit('toggle')
}

const isSidebarVisible = computed(() => props.isOpen)

const { width } = useWindowSize()
const isMobileView = computed(() => width.value < 960)
watch(isMobileView, (isMobile) => {
  if (isMobile && props.isOpen) {
    emit('toggle')
  }
})
const { combinedChatList } = storeToRefs(useChatStore())
const isMcpConnectorsEnabled = usePosthogFlag(PosthogFeatureFlags.McpConnectors)
useEventListeners().registerSidebarToggleKeyboardEvent(toggleSidebar)
</script>

<style lang="scss" scoped>
.TheSidebar {
  width: var(--sidebar-width);
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  // The outer element stays mounted (and full-sized) even while closed so
  // the slide transition can animate it out — without this, its invisible
  // box would still intercept clicks on whatever sits behind it.
  pointer-events: none;

  @include md {
    position: relative;
  }

  .TheSidebar__content {
    display: flex;
    flex-direction: column;
    gap: var(---fds-global-space-sm);
    // position: fixed;
    z-index: 100;
    height: 100%;
    padding: var(---fds-global-space-md) var(---fds-global-space-sm);
    border-right: 1px solid var(--border-light);
    background: var(--bg-primary);
    pointer-events: auto;
    z-index: 100;
  }

  .TheSidebar__nav {
    min-height: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
  }

  .TheSidebar__scrollArea {
    @include customScrollbar;
    min-height: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    // Vertical only. `customScrollbar` sets `overflow: auto` on both axes and sizes
    // the bar with `width`, so anything that overruns the sidebar sideways would open
    // a full-height, unstyled horizontal scrollbar under the list.
    overflow: hidden auto;
    margin-right: calc(-1 * var(---fds-global-space-sm));
    width: calc(100% + var(---fds-global-space-sm));
    padding-right: var(---fds-global-space-sm);

    // Scrollbar thumb stays hidden until the sidebar is hovered.
    &::-webkit-scrollbar-thumb {
      background-color: transparent;
    }

    @supports not selector(::-webkit-scrollbar) {
      scrollbar-color: transparent transparent;
    }
  }

  &:hover .TheSidebar__scrollArea {
    &::-webkit-scrollbar-thumb {
      background-color: var(--interactive-bg-secondary-press);

      &:hover,
      &:active {
        background-color: var(--icon-secondary);
      }
    }

    @supports not selector(::-webkit-scrollbar) {
      scrollbar-color: var(--interactive-bg-secondary-press) transparent;
    }
  }

  .TheSidebar__section {
    display: flex;
    flex-direction: column;
    margin-top: var(---fds-global-space-md);

    &.TheSidebar__section--top {
      margin-top: 0;
    }
  }

  .TheSidebar__section-title {
    @include font-caption-medium;
    color: var(--text-tertiary);
    font-feature-settings:
      'liga' off,
      'clig' off;
    margin: 0;
    padding: 0 var(---fds-global-space-xs) var(---fds-global-space-xs);
  }

  .TheSidebar__section-content {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
}

.TheSidebar__overlay {
  pointer-events: auto;
  z-index: -1 !important;
}
</style>
